import type { EnterpriseInfo } from '@/services/AccountServer/types'
import type { IUserInfo } from '@/services/UserServer/types'
import { getEnterpriseFuelMode, type EnterpriseFuelMode } from '@/utils/enterpriseTrade'

export type EnterpriseWorkspaceMode = 'local' | 'legacy-cross' | 'global'

/** 用户信息里的待加入企业 id 是邀请是否仍待处理的唯一状态源。 */
export function hasPendingEnterpriseInvitation(user?: Partial<IUserInfo> | null) {
  const inviteBizTeamId = Number(user?.inviteBizTeamId || 0)
  return Number.isFinite(inviteBizTeamId) && inviteBizTeamId > 0
}

/** 工作空间关系独立于付费套餐家族，不能用它决定加油包或升级价格。 */
export function getEnterpriseWorkspaceMode(enterprise?: Partial<EnterpriseInfo> | null): EnterpriseWorkspaceMode {
  if (Number(enterprise?.serviceLevel || 0) >= 5) return 'global'
  if (enterprise?.regionMode === 'CROSS_REGION' && enterprise.legacyMigrationNo?.trim()
    && !enterprise.globalContractNo?.trim()) return 'legacy-cross'
  return 'local'
}

export interface EnterpriseManagementAccess {
  hasEnterprise: boolean
  planMode: EnterpriseFuelMode
  workspaceMode: EnterpriseWorkspaceMode
  hasActivePlan: boolean
  isCreator: boolean
  isManager: boolean
  canInviteOrAdd: boolean
  canDissolve: boolean
  canManageBalanceAlert: boolean
  canPurchaseFuel: boolean
  showSeats: boolean
  showWorkspaceQuota: boolean
  readonlyMembers: boolean
}

export interface EnterpriseMemberRefreshContext {
  token?: string | null
  userId?: number | string | null
  bizTeamId?: number | string | null
  enterpriseId?: number | string | null
  serviceLevel?: number | string | null
  serviceEndTime?: string | null
  globalContractNo?: string | null
  regionMode?: string | null
  legacyMigrationNo?: string | null
  currentRegion?: string | null
  menu?: string | null
}

/**
 * 将企业成员页的响应式对象收敛为稳定键。
 * 同一企业信息被后台刷新成新对象时键值不变，避免把普通刷新误判成企业切换。
 */
export function getEnterpriseMemberRefreshKeys(context: EnterpriseMemberRefreshContext) {
  const normalize = (value: unknown) => String(value ?? '')
  const scopeKey = [
    context.token,
    context.userId,
    context.bizTeamId,
    context.enterpriseId,
    context.currentRegion,
  ].map(normalize).join('\u001f')
  const refreshKey = [
    scopeKey,
    context.serviceLevel,
    context.serviceEndTime,
    context.globalContractNo,
    context.regionMode,
    context.legacyMigrationNo,
    context.menu,
  ].map(normalize).join('\u001f')
  return { scopeKey, refreshKey }
}

export type EnterpriseInvitationState = 'member' | 'pending' | 'expired'
export type EnterpriseMemberOperationKey = 'edit' | 'delete' | 'quit' | 'revoke' | 'copy-link' | 'readd' | 'remove-invite'

export interface EnterpriseMemberOperationContext {
  readonly: boolean
  operatorRole: number
  targetRole: number
  isSelf: boolean
  invitationState?: EnterpriseInvitationState
  /** 待接受的链接邀请带有可复制的邀请地址；邮箱直接添加的待确认成员没有链接。 */
  hasInviteUrl?: boolean
}

export interface EnterpriseMemberCountSummary {
  joined: number
  pending: number
  expired: number
  seatUsed: number
  totalMembers: number
}

interface EnterpriseMemberStateSource {
  status?: number | string | null
  recordType?: string | null
  inviteStatus?: string | null
  invitationStatus?: string | null
}

/** 后端兼容返回 member/invitation 两类记录，页面统一收敛成三个展示状态。 */
export function getEnterpriseMemberState(member?: EnterpriseMemberStateSource | null): EnterpriseInvitationState {
  const invitationStatus = String(member?.inviteStatus || member?.invitationStatus || '').toUpperCase()
  if (invitationStatus === 'EXPIRED') return 'expired'
  if (['PENDING', 'ACTIVE', 'WAITING'].includes(invitationStatus)) return 'pending'
  if (String(member?.recordType || '').toUpperCase() === 'INVITATION') {
    return invitationStatus === 'EXPIRED' ? 'expired' : 'pending'
  }
  return Number(member?.status) === 0 ? 'pending' : 'member'
}

export function getEnterpriseMemberCountSummary(
  members: EnterpriseMemberStateSource[],
): EnterpriseMemberCountSummary {
  return members.reduce<EnterpriseMemberCountSummary>((summary, member) => {
    const state = getEnterpriseMemberState(member)
    if (state === 'member') summary.joined += 1
    if (state === 'pending') summary.pending += 1
    if (state === 'expired') summary.expired += 1
    summary.seatUsed = summary.joined + summary.pending
    summary.totalMembers = summary.joined + summary.pending
    return summary
  }, { joined: 0, pending: 0, expired: 0, seatUsed: 0, totalMembers: 0 })
}

export function getEnterpriseMemberOperationKeys(
  context: EnterpriseMemberOperationContext,
): EnterpriseMemberOperationKey[] {
  const invitationState = context.invitationState || 'member'
  if (invitationState === 'pending') {
    if (context.readonly) return []
    // 复制按钮排在撤销右侧，便于关闭邀请弹窗后再次分发同一条链接
    return context.hasInviteUrl ? ['revoke', 'copy-link'] : ['revoke']
  }
  if (invitationState === 'expired') return context.readonly ? [] : ['readd', 'remove-invite']
  // 创建者是企业所有权锚点；无论是否来自另一空间，都不能编辑或删除。
  if (context.targetRole === 1) return []
  if (context.isSelf) {
    return context.operatorRole === 2 || context.operatorRole === 3 ? ['quit'] : []
  }
  // 套餐失效只限制管理操作，管理员和普通成员仍应能退出企业。
  if (context.readonly) return []
  if (context.operatorRole === 1) return ['edit', 'delete']
  if (context.operatorRole === 2 && context.targetRole === 3) return ['edit']
  return []
}

export function getEnterpriseManagementAccess(
  enterprise: Partial<EnterpriseInfo> | null | undefined,
  bizTeamId: number | null | undefined,
  memberRole: number | null | undefined,
  now = Date.now(),
): EnterpriseManagementAccess {
  const hasEnterprise = Boolean(bizTeamId || enterprise?.id)
  const planMode = hasEnterprise ? getEnterpriseFuelMode(enterprise, now) : 'none'
  const hasActivePlan = planMode !== 'none'
  const workspaceMode = getEnterpriseWorkspaceMode(enterprise)
  const writable = !['MIGRATING', 'HISTORY_READONLY'].includes(enterprise?.migrationServiceState || '')
  const role = Number(memberRole || 0)
  const isCreator = hasEnterprise && role === 1
  const isManager = hasEnterprise && (role === 1 || role === 2)

  return {
    hasEnterprise,
    planMode,
    workspaceMode,
    hasActivePlan,
    isCreator,
    isManager,
    canInviteOrAdd: hasActivePlan && isManager && writable,
    canDissolve: isCreator,
    canManageBalanceAlert: hasActivePlan && isManager,
    canPurchaseFuel: hasActivePlan && isCreator,
    showSeats: hasActivePlan,
    showWorkspaceQuota: hasActivePlan && workspaceMode !== 'local',
    readonlyMembers: hasEnterprise && (!hasActivePlan || !writable),
  }
}
